import { convexQuery } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { CandidateStatus } from '@cvx/features/candidates/candidates_db'
import { cn } from '@rekrytering/ui/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

const CANDIDATE_STATUS_STYLES: Record<CandidateStatus, string> = {
  invited:
    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  started: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  completed:
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  expired: 'bg-gray-100 text-gray-500 dark:bg-gray-900/30 dark:text-gray-400',
  disqualified: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  advanced:
    'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
}

export const PublicShortlistPage = () => {
  const { t } = useTranslation()
  const { shareToken } = useParams({ from: '/sl/$shareToken' })

  const { data } = useQuery(
    convexQuery(api.features.candidates.candidates.getPublicShortlist, {
      shareToken,
    }),
  )

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-primary/40">{t('jobs.shortlist.empty')}</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <div className="mb-8 flex flex-col gap-1">
        <p className="text-xs font-medium uppercase tracking-widest text-primary/40">
          {t('jobs.shortlist.jobLabel')}
        </p>
        <h1 className="text-2xl font-semibold text-primary">{data.jobTitle}</h1>
        <p className="text-sm text-primary/50">{t('jobs.shortlist.title')}</p>
      </div>

      {data.candidates.length === 0 ? (
        <p className="py-12 text-center text-sm text-primary/40">
          {t('jobs.shortlist.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/50 text-left text-xs text-primary/50">
                <th className="px-4 py-3 font-medium">
                  {t('jobs.shortlist.name')}
                </th>
                <th className="px-4 py-3 font-medium">
                  {t('jobs.shortlist.status')}
                </th>
                <th className="px-4 py-3 font-medium">
                  {t('jobs.shortlist.score')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.candidates.map((candidate) => (
                <tr
                  key={candidate._id}
                  className="border-b border-border/50 last:border-0"
                >
                  <td className="px-4 py-3 font-medium text-primary">
                    {candidate.name}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        CANDIDATE_STATUS_STYLES[candidate.status],
                      )}
                    >
                      {candidate.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-primary">
                    {candidate.averageScore !== null
                      ? Math.round(candidate.averageScore)
                      : t('jobs.shortlist.noScore')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
